import { AfterViewInit, Component, OnInit, SecurityContext, ViewContainerRef, ViewEncapsulation, ChangeDetectionStrategy, inject, viewChild, input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SbModalButton, SbModalOptions } from './sb-modal.types';
import { inputGiver } from './sb-modal-inputs';
import { NgTemplateOutlet } from '@angular/common';
import { SbButtonComponent } from '../sb-button/sb-button.component';

@Component({
  selector: 'app-sb-modal-host',
  templateUrl: './sb-modal-host.component.html',
  styleUrls: ['./sb-modal-host.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, SbButtonComponent]
})
export class SbModalHostComponent implements OnInit, AfterViewInit {
  readonly activeModal = inject(NgbActiveModal);
  private readonly sanitizer = inject(DomSanitizer);

  readonly options = input<SbModalOptions>({});

  readonly dynamicHost = viewChild('dynamicHost', { read: ViewContainerRef });

  buttons: SbModalButton[] = [];
  safeIframeUrl: SafeResourceUrl | null = null;
  /** An iframed page is always escapable to its own tab unless the caller overrides it. */
  externalUrl: string | null = null;

  ngOnInit(): void {
    this.buttons = this.resolveButtons();
    const options = this.options();
    if (options.iframeUrl) {
      const safe = this.sanitizer.sanitize(SecurityContext.URL, options.iframeUrl);
      this.safeIframeUrl = safe
        ? this.sanitizer.bypassSecurityTrustResourceUrl(safe)
        : null;
    }
    const external = options.externalUrl || options.iframeUrl || null;
    this.externalUrl = external
      ? this.sanitizer.sanitize(SecurityContext.URL, external)
      : null;
  }

  ngAfterViewInit(): void {
    const dynamicHost = this.dynamicHost();
    const options = this.options();
    if (!options.bodyComponent || !dynamicHost) {
      return;
    }
    const ref = dynamicHost.createComponent(options.bodyComponent as never);
    const instance = ref.instance as Record<string, any> | null;
    if (instance && typeof instance === 'object') {
      const give = inputGiver(ref);
      Object.entries((options.modalData as object) || {}).forEach(([key, value]) => give(key, value));
      // Provide close helpers, but never clobber component outputs of the
      // same name (e.g. entity forms exposing `closeModal` emitters).
      if (!this.isEmitterLike(instance['closeModal'])) {
        give('closeModal', (result?: unknown) => this.activeModal.close(result));
      }
      if (!this.isEmitterLike(instance['dismissModal'])) {
        give('dismissModal', (reason?: unknown) => this.activeModal.dismiss(reason));
      }
      // Generic entity-form contract: close the modal when the form finishes.
      if (this.isEmitterLike(instance['modalDone'])) {
        instance['modalDone'].subscribe((result: unknown) => this.activeModal.close(result));
      }
      ref.changeDetectorRef.markForCheck();
    }
  }

  private isEmitterLike(value: any): boolean {
    return !!value && typeof value.subscribe === 'function';
  }

  get showFooter(): boolean {
    return this.options().showFooter !== false && this.buttons.length > 0;
  }

  onButton(button: SbModalButton): void {
    if (button.dismiss) {
      this.activeModal.dismiss(button.closeValue ?? button.id);
      return;
    }
    this.activeModal.close(button.closeValue ?? button.id);
  }

  private resolveButtons(): SbModalButton[] {
    const options = this.options();
    if (options.buttons?.length) {
      return options.buttons;
    }
    if (options.variant === 'confirm') {
      return [
        {
          id: 'cancel',
          label: options.cancelLabel || 'Cancel',
          style: 'default',
          dismiss: true,
        },
        {
          id: 'confirm',
          label: options.confirmLabel || 'Confirm',
          style: 'primary',
          closeValue: true,
        },
      ];
    }
    if (options.variant === 'iframe') {
      return [
        {
          id: 'close',
          label: options.cancelLabel || 'Close',
          style: 'default',
          dismiss: true,
        },
      ];
    }
    if (options.variant === 'form' || options.variant === 'default' || !options.variant) {
      return [
        {
          id: 'cancel',
          label: options.cancelLabel || 'Cancel',
          style: 'default',
          dismiss: true,
        },
        {
          id: 'save',
          label: options.confirmLabel || 'Save',
          style: 'primary',
          closeValue: 'save',
        },
      ];
    }
    return [
      {
        id: 'close',
        label: options.cancelLabel || 'Close',
        style: 'default',
        dismiss: true,
      },
    ];
  }
}
